import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { PortableText } from '@portabletext/react'
import { formatInTimeZone } from 'date-fns-tz'
import React from 'react'
import {
	type ActionFunctionArgs,
	data,
	Form,
	Link,
	useActionData,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import NewsletterWelcome from '#app/components/emails/newsletter-welcome.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import {
	GeneralDocument,
	generalDocumentQuery,
	type ResolvedDoc,
} from '#app/components/general-document.tsx'
import { SanityImage } from '#app/components/ui/SanityImage.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { Logo } from '#app/root.tsx'
import {
	type Event,
	type SanityImageCrop,
	type SanityImageHotspot,
	type SundayUpdate,
	type Venue,
} from '#app/types/sanity.types.ts'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail, upsertResendContact } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { safeFetch } from '#app/utils/sanity.server.ts'
import { createToastHeaders } from '#app/utils/toast.server.ts'
import { type Route } from './+types/index.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'Steel City Backgammon' },
]

type ResolvedVenueThumbnail = {
	asset: {
		_id: string
		metadata?: {
			dimensions: {
				width: number
				height: number
				aspectRatio: number
			}
		}
	}
	hotspot?: SanityImageHotspot
	crop?: SanityImageCrop
	alt?: string
	_type: 'image'
}

type ResolvedComingSoonCard = {
	_key: string
	displayName?: string
	date?: string
	displayDate?: string
	location?: string
	description?: string
	website?: string
	thumbnail?: {
		asset?: {
			_id: string
			metadata?: {
				dimensions: { width: number; height: number; aspectRatio: number }
			}
		}
		hotspot?: SanityImageHotspot
		crop?: SanityImageCrop
		_type: 'image'
	}
}

type NextEvent = Omit<Event, 'location'> & {
	venue:
		| (Omit<Venue, 'thumbnail'> & { thumbnail?: ResolvedVenueThumbnail })
		| null
}

export async function loader({ request }: Route.LoaderArgs) {
	// The Fly health check HEAD-fetches "/" every 10s. Skip Sanity for it: it
	// otherwise burns the Sanity request quota around the clock, and would
	// couple the machine's health to Sanity's uptime — a Sanity outage failing
	// the health check would take the whole site out of rotation.
	if (request.headers.get('X-Healthcheck') === 'true') {
		return {
			nextUp: null,
			sundayUpdate: null,
			comingSoon: null,
			helpWantedAbbrev: null,
		}
	}

	const eventQuery = `*[_type == "event" && date >= $today] | order(date asc)[0] {
    _id,
    name,
    displayName,
    eventType,
    useDate,
    date,
    dateRange,
    description,
    extendedDescription[]{
      ...,
      _type == "image" => {
        ...,
        asset-> {
          _id,
          url,
          metadata {
            dimensions
          }
        }
      }
    },
    "venue": location -> {
      _id,
      displayName,
      address,
      city,
      state,
      zip,
      map,
      website,
      thumbnail {
        ...,
        asset -> {
          _id,
          metadata {
            dimensions {
              width,
              height,
              aspectRatio,
            }
          }
        }
      }
    }
  }`

	const sundayUpdateQuery = `*[_type == "sundayUpdate" && visible == true][0] {
    _id,
    visible,
    title,
    description,
    body[]{
      ...,
      _type == "image" => {
        ...,
        asset-> {
          _id,
          url,
          metadata {
            dimensions
          }
        }
      }
    }
  }`

	const comingSoonQuery = `*[_type == "comingSoon"][0] {
    "events": events[date >= $today] | order(date asc) {
      _key,
      displayName,
      date,
      displayDate,
      location,
      description,
      website,
      thumbnail {
        ...,
        asset-> {
          _id,
          url,
          metadata { dimensions }
        }
      }
    }
  }`

	// When this doc exists, it takes over the "Next Up!" slot with a venue-search
	// plea (events are paused). Deleting/unpublishing it in Sanity restores the
	// normal Next Up! behavior — the content is the switch, no code change needed.
	const helpWantedAbbrevQuery = `*[_type == "generalDocument" && name == "help-wanted-abbrev"][0]${generalDocumentQuery}`

	const today = new Date().toISOString().split('T')[0]
	const [nextUp, sundayUpdate, comingSoon, helpWantedAbbrev] =
		await Promise.all([
			safeFetch<NextEvent | null>(eventQuery, { today }, null),
			safeFetch<SundayUpdate | null>(sundayUpdateQuery, {}, null),
			safeFetch<{ events: ResolvedComingSoonCard[] } | null>(
				comingSoonQuery,
				{ today },
				null,
			),
			safeFetch<ResolvedDoc | null>(helpWantedAbbrevQuery, {}, null),
		])
	return { nextUp, sundayUpdate, comingSoon, helpWantedAbbrev }
}

const ContactSchema = z.object({
	firstName: z
		.string({ required_error: 'First name is required' })
		.trim()
		.max(50, 'First name is too long'),
	lastName: z.string().trim().max(50, 'Last name is too long').optional(),
	email: z
		.string({ required_error: 'Email is required' })
		.trim()
		.email('Invalid email address')
		.toLowerCase(),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = parseWithZod(formData, {
		schema: ContactSchema,
	})

	if (submission.status !== 'success') {
		return { result: submission.reply() }
	}

	const { email, firstName, lastName } = submission.value

	// 1. Look for an existing Contact by email
	const existingContact = await prisma.contact.findUnique({
		where: { email },
	})

	const newsletterSegmentId = process.env.RESEND_NEWSLETTER_SEGMENT ?? ''

	if (existingContact) {
		// 2. Update their name but do not send a welcome email
		await prisma.contact.update({
			where: { email },
			data: { firstName, lastName },
		})

		await upsertResendContact({
			email,
			firstName,
			segmentId: newsletterSegmentId,
		}).catch((e) => console.error('Resend upsert failed:', e))

		const toastHeaders = await createToastHeaders({
			title: `Welcome back, ${firstName}!`,
			description: 'Your email was already on the list. We updated your name.',
			type: 'success',
		})
		return data({ result: submission.reply() }, { headers: toastHeaders })
	}

	// 3. Create the new Contact since they don't exist yet
	await prisma.contact.create({
		data: { email, firstName, lastName },
	})

	await upsertResendContact({
		email,
		firstName,
		segmentId: newsletterSegmentId,
	}).catch((e) => console.error('Resend upsert failed:', e))

	// 4. Send the fancy email to new contacts
	await sendEmail({
		to: email,
		from: 'Steel City Backgammon <info+news@steelcitybg.com>',
		subject: 'Welcome to Steel City Backgammon!',
		react: NewsletterWelcome({ firstName }),
	})

	const toastHeaders = await createToastHeaders({
		title: `You're all set, ${firstName}!`,
		description: 'Check your inbox for a welcome email.',
		type: 'success',
	})
	return data({ result: submission.reply() }, { headers: toastHeaders })
}

export default function Index({ loaderData }: Route.ComponentProps) {
	const leftColRef = React.useRef<HTMLDivElement>(null)
	const comingSoonHeadingRef = React.useRef<HTMLDivElement>(null)
	const [boxMaxHeight, setBoxMaxHeight] = React.useState<number | undefined>()

	// Measure the left column height and subtract the Coming Soon heading height
	// + gap-4 (16px) to get the exact max-height for the scrollable box.
	React.useEffect(() => {
		const el = leftColRef.current
		if (!el) return
		const update = () => {
			// Only constrain in the 2-column layout (md = 768px+). On mobile the
			// CSS max-h-[1000px] class handles the hard cap without JS measurement.
			if (!window.matchMedia('(min-width: 768px)').matches) {
				setBoxMaxHeight(undefined)
				return
			}
			const leftH = el.offsetHeight
			const headingH = comingSoonHeadingRef.current?.offsetHeight ?? 0
			const derived = leftH - headingH - 16
			setBoxMaxHeight(derived > 0 ? derived : undefined)
		}
		// ResizeObserver catches element size changes; the matchMedia listener
		// catches the exact moment we cross the md breakpoint.
		const observer = new ResizeObserver(update)
		const mq = window.matchMedia('(min-width: 768px)')
		observer.observe(el)
		mq.addEventListener('change', update)
		update()
		return () => {
			observer.disconnect()
			mq.removeEventListener('change', update)
		}
	}, [])

	return (
		<main className="font-poppins mt-4 flex h-full w-[calc(100%)] flex-col items-center justify-center gap-6 px-4 sm:px-8">
			<Logo width={120} height={120} />
			<div className="text-3xl text-black sm:text-4xl md:text-5xl dark:text-white">
				Steel City Backgammon
			</div>
			<div className="flex flex-col items-center">
				<div className="px-5 text-center text-xl text-gray-500 dark:text-gray-400">
					Pittsburgh's place to play backgammon, have fun, and make new friends.
				</div>
				<div className="text-center text-xl text-gray-500 dark:text-gray-400">
					Join in on the fun! All skill levels are welcome.
				</div>
			</div>

			{/* 2-column section: Next Up + Sunday Update (left) | Coming Soon (right) */}
			<div
				className={`flex w-full max-w-5xl flex-col gap-6 ${loaderData.comingSoon?.events?.length ? 'md:flex-row md:items-start' : ''}`}
			>
				{/* Left column: Next Up + Sunday Update */}
				<div
					ref={leftColRef}
					data-testid="coming-soon-left-col"
					className="flex flex-col gap-6 md:flex-[7]"
				>
					{loaderData.helpWantedAbbrev ? (
						<div className="flex flex-col gap-4">
							<div className="text-center text-2xl text-black sm:text-4xl dark:text-white">
								Help Wanted!
							</div>
							<div className="bg-muted flex w-full flex-col gap-4 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
								{/* Card owns the padding; drop the component's own px so the
								    text aligns to the card inset like the other sections. */}
								<GeneralDocument
									doc={loaderData.helpWantedAbbrev}
									className="px-0"
								/>
							</div>
						</div>
					) : (
						<div className="flex flex-col gap-4">
							<div className="text-center text-2xl text-black sm:text-4xl dark:text-white">
								Next Up!
							</div>
							{loaderData.nextUp ? (
								<EventSummary event={loaderData.nextUp} />
							) : (
								<div className="p-4 text-gray-500">
									No events scheduled right now. Check back soon!
								</div>
							)}
						</div>
					)}
					{loaderData.sundayUpdate ? (
						<div className="flex flex-col gap-4">
							<div className="text-center text-2xl text-black sm:text-4xl dark:text-white">
								{loaderData.sundayUpdate.title}
							</div>
							<SundayUpdateSection update={loaderData.sundayUpdate} />
						</div>
					) : null}
				</div>

				{/* Right column: Coming Soon heading above the box, matching Next Up layout */}
				{loaderData.comingSoon?.events?.length ? (
					<div className="flex flex-col gap-4 md:flex-[3]">
						<div
							ref={comingSoonHeadingRef}
							data-testid="coming-soon-heading"
							className="text-center text-2xl text-black sm:text-4xl dark:text-white"
						>
							Coming Soon
						</div>
						<ComingSoonSection
							events={loaderData.comingSoon.events}
							maxHeight={boxMaxHeight}
						/>
					</div>
				) : null}
			</div>

			<div className="mt-6 text-center text-2xl text-black sm:text-4xl dark:text-white">
				Stay Updated
			</div>
			<div className="max-w-[500px] px-5 text-center text-xl text-gray-500 dark:text-gray-400">
				Why not add yourself to our email list?
				<br />
				We'll notify you about backgammon events and website updates.
			</div>
			<div className="max-w-[500px] px-5 text-center text-xl text-gray-500 dark:text-gray-400">
				This is a human-run group, so you don't have to
				<br />
				worry about ads or spam in your inbox, just fun backgammon stuff!
			</div>

			<NewsletterSignup />
		</main>
	)
}

function ComingSoonSection({
	events,
	maxHeight,
}: {
	events: ResolvedComingSoonCard[]
	maxHeight?: number
}) {
	return (
		<div
			data-testid="coming-soon-box"
			className="flex max-h-[1000px] flex-col rounded-2xl border border-indigo-300 bg-indigo-50 p-4 md:max-h-none dark:border-indigo-700 dark:bg-indigo-950/40"
			style={maxHeight ? { maxHeight } : undefined}
		>
			<div className="min-h-0 flex-1 overflow-y-auto [scrollbar-color:var(--color-indigo-300)_transparent] [scrollbar-width:thin] dark:[scrollbar-color:var(--color-indigo-700)_transparent]">
				{events.map((card, i) => (
					<div key={card._key}>
						<div className="flex gap-3">
							<div className="flex flex-1 flex-col gap-0.5">
								{card.website ? (
									<a
										href={card.website}
										target="_blank"
										rel="noopener noreferrer"
										className="text-sm font-semibold text-indigo-800 hover:underline dark:text-indigo-200"
									>
										{card.displayName}
									</a>
								) : (
									<span className="text-sm font-semibold text-indigo-800 dark:text-indigo-200">
										{card.displayName}
									</span>
								)}
								<div className="text-xs text-indigo-600 dark:text-indigo-400">
									{card.displayDate ?? card.date}
								</div>
								{card.location ? (
									<div className="text-xs text-indigo-500">{card.location}</div>
								) : null}
							</div>
							{card.thumbnail?.asset ? (
								<div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg">
									<SanityImage
										source={card.thumbnail as any}
										alt={card.displayName ?? 'Coming Soon'}
										className="h-full w-full object-cover"
									/>
								</div>
							) : null}
						</div>
						{card.description ? (
							<p className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
								{card.description}
							</p>
						) : null}
						{i < events.length - 1 ? (
							<hr className="my-3 border-indigo-200 dark:border-indigo-800" />
						) : null}
					</div>
				))}
			</div>
		</div>
	)
}

// Hoisted to module scope so the object identity is stable across renders.
// If defined inline, PortableText remounts the image renderer on every render,
// recreating the <img> nodes — which combined with the left-column
// ResizeObserver produced an infinite reflow/reload loop (image flicker).
const sundayUpdatePortableTextComponents = {
	types: {
		image: ({ value }: any) => {
			if (!value.asset) return null
			return (
				<figure className="my-4">
					<SanityImage
						source={value}
						alt={value.alt || 'Sunday Update Image'}
						className="w-full rounded-xl shadow-lg"
					/>
					{value.caption ? (
						<figcaption className="mt-1 text-center text-sm text-slate-500 dark:text-slate-400">
							{value.caption}
						</figcaption>
					) : null}
				</figure>
			)
		},
	},
	marks: {
		link: ({ children, value }: any) => (
			<a href={value.href} target="_blank" rel="noopener noreferrer">
				{children}
			</a>
		),
	},
	block: {
		normal: ({ children }: any) => (
			<p className="mb-4 leading-relaxed">{children}</p>
		),
		h1: ({ children }: any) => (
			<h2 className="mt-8 mb-4 text-2xl font-bold">{children}</h2>
		),
	},
}

function SundayUpdateSection({ update }: { update: SundayUpdate }) {
	return (
		<div className="bg-muted mx-auto flex w-full max-w-5xl flex-col gap-4 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
			{update.description ? (
				<div className="text-md text-slate-600 dark:text-slate-400">
					{update.description}
				</div>
			) : null}
			{update.body && update.body.length > 0 ? (
				<div className="prose prose-slate dark:prose-invert max-w-none">
					<PortableText
						value={update.body}
						components={sundayUpdatePortableTextComponents}
					/>
				</div>
			) : null}
		</div>
	)
}

// Hoisted to module scope for a stable identity across renders — see the note
// on sundayUpdatePortableTextComponents.
const eventPortableTextComponents = {
	types: {
		image: ({ value }: any) => {
			if (!value.asset) return null
			return (
				<figure className="my-4 w-full">
					<SanityImage
						source={value}
						alt={value.alt || 'Event Image'}
						className="w-full rounded-xl shadow-lg"
					/>
					{value.caption ? (
						<figcaption className="mt-1 text-sm text-slate-500 dark:text-slate-400">
							{value.caption}
						</figcaption>
					) : null}
				</figure>
			)
		},
	},
	marks: {
		link: ({ children, value }: any) => (
			<a href={value.href} target="_blank" rel="noopener noreferrer">
				{children}
			</a>
		),
	},
	block: {
		normal: ({ children }: any) => (
			<p className="mb-4 leading-relaxed">{children}</p>
		),
		h1: ({ children }: any) => (
			<h2 className="mt-8 mb-4 text-2xl font-bold">{children}</h2>
		),
	},
}

function EventSummary({ event }: { event: NextEvent }) {
	const formattedDate =
		event.useDate === 'date'
			? formatInTimeZone(
					new Date(event.date!),
					'America/New_York',
					'eee, MMM d, h:mm aaa',
				)
			: null
	const remainingRichText = event.extendedDescription

	return (
		<div className="bg-muted mx-auto flex w-full max-w-5xl flex-col gap-4 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
			<div className="flex gap-4">
				<div className="flex flex-1 flex-col">
					<div className="text-2xl sm:text-3xl">{event.displayName}</div>
					{event.eventType ? (
						<div className="mt-1 text-slate-600 dark:text-slate-400">
							{event.eventType.charAt(0).toUpperCase() +
								event.eventType.slice(1)}{' '}
							Backgammon
						</div>
					) : null}
					<div className="text-bold mt-2 text-xl">
						{formattedDate ? `${formattedDate}` : (event.dateRange ?? '')}
					</div>
					<div className="text-md mt-2">{event.venue?.displayName}</div>
					<div className="text-md">{event.venue?.address}</div>
					<div className="text-md">
						{event.venue?.city} {event.venue?.zip}
					</div>
					{(event.venue?.map || event.venue?.website) && (
						<div className="mt-2 hidden gap-2 sm:flex">
							{event.venue?.map && (
								<Link
									to={event.venue.map}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center gap-2 rounded-md bg-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 no-underline transition-colors hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-200"
								>
									Directions
								</Link>
							)}
							{event.venue?.website && (
								<Link
									to={event.venue.website}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center gap-2 rounded-md bg-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 no-underline transition-colors hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-200"
								>
									Website
								</Link>
							)}
						</div>
					)}
				</div>
				<div className="basis-1/3">
					{event.venue?.thumbnail && (
						<SanityImage
							source={event.venue?.thumbnail}
							alt={'Mondays Brewing'}
							className="h-full w-full rounded-xl object-cover shadow-lg"
						/>
					)}
				</div>
			</div>
			{(event.venue?.map || event.venue?.website) && (
				<div className="flex gap-2 sm:hidden">
					{event.venue?.map && (
						<Link
							to={event.venue.map}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-2 rounded-md bg-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 no-underline transition-colors hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-200"
						>
							Directions
						</Link>
					)}
					{event.venue?.website && (
						<Link
							to={event.venue.website}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-2 rounded-md bg-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 no-underline transition-colors hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-200"
						>
							Website
						</Link>
					)}
				</div>
			)}
			<div className="text-md">{event.description}</div>
			<div className="prose prose-slate dark:prose-invert max-w-none">
				<PortableText
					value={remainingRichText || []}
					components={eventPortableTextComponents}
				/>
			</div>
		</div>
	)
}

function NewsletterSignup() {
	const isPending = useIsPending()
	const actionData = useActionData<typeof action>()

	const [form, fields] = useForm({
		id: 'newsletter-signup-form',
		constraint: getZodConstraint(ContactSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ContactSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="mx-auto w-full max-w-xl rounded-xl bg-slate-50 p-6 dark:bg-slate-700">
			<Form method="POST" {...getFormProps(form)}>
				<HoneypotInputs />

				<div className="grid grid-cols-1 gap-x-4 sm:grid-cols-3">
					<Field
						labelProps={{ children: 'First Name *' }}
						inputProps={{
							...getInputProps(fields.firstName, { type: 'text' }),
						}}
						errors={fields.firstName.errors}
					/>

					<Field
						className="sm:col-span-2"
						labelProps={{ children: 'Last Name' }}
						inputProps={{
							...getInputProps(fields.lastName, { type: 'text' }),
						}}
						errors={fields.lastName.errors}
					/>

					<Field
						className="sm:col-span-3"
						labelProps={{ children: 'Email *' }}
						inputProps={{
							...getInputProps(fields.email, { type: 'email' }),
						}}
						errors={fields.email.errors}
					/>

					<StatusButton
						className="mx-auto w-50 sm:col-start-2 sm:mx-0 sm:w-full"
						status={isPending ? 'pending' : (form.status ?? 'idle')}
						type="submit"
						disabled={isPending}
					>
						Sign Me Up!
					</StatusButton>
				</div>

				<ErrorList errors={form.errors} id={form.errorId} />
			</Form>
		</div>
	)
}
